import { useState, useEffect } from 'react';
import { placeOrder } from '../../services/OrdersService';
import { getBalance } from '../../services/ExchangeService';
import { getSymbol } from '../../services/SymbolsService';
import Toast from '../../components/Toast';
import './SellScreen.css';

function parseBalanceQty(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'object')
        return parseFloat(value.available ?? value.availableBalance ?? value.free ?? value.current ?? 0) || 0;
    return parseFloat(value) || 0;
}

function roundToStep(qty, stepSize) {
    const amount = parseFloat(qty);
    const step = parseFloat(stepSize);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    if (!Number.isFinite(step) || step <= 0) return amount;
    const rounded = Math.floor((amount + 1e-12) / step) * step;
    const decimals = (String(step).split('.')[1] || '').replace(/0+$/, '').length;
    return parseFloat(rounded.toFixed(Math.max(decimals, 0)));
}

function maxSellableQty(available, coin, stepSize) {
    const avail = parseFloat(available) || 0;
    if (avail <= 0) return 0;
    let maxQty = avail;
    if (String(coin || '').toUpperCase() === 'BNB') {
        const step = parseFloat(stepSize) || 0;
        const reserve = Math.max(avail * 0.001, step);
        maxQty = Math.max(0, avail - reserve);
    }
    return roundToStep(maxQty, stepSize);
}

function SellScreen({ coin, price: initialPrice, balance: initialBalance, onClose, onSuccess }) {
    const [step, setStep] = useState(1);
    const [brlAmount, setBrlAmount] = useState('');
    const [quantity, setQuantity] = useState('0');
    const [totalUSDT, setTotalUSDT] = useState('0');
    const [orderType] = useState('MARKET');
    const [limitPrice] = useState('');
    const [price, setPrice] = useState(initialPrice);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [usdtBrlRate, setUsdtBrlRate] = useState(0);
    const [liveBalance, setLiveBalance] = useState(initialBalance || null);
    const [stepSize, setStepSize] = useState(0);
    const [minNotional, setMinNotional] = useState(0);

    const availableBalance = parseBalanceQty(liveBalance && liveBalance.available);
    const lockedBalance = parseBalanceQty(liveBalance && liveBalance.onOrder);
    const maxQuantity = maxSellableQty(availableBalance, coin, stepSize);
    const hasBalance = maxQuantity > 0;
    const isCustody = localStorage.getItem('isCustody') === 'true';
    const quoteAsset = isCustody ? 'USDC' : 'USDT';
    const symbol = `${coin}${quoteAsset}`;

    useEffect(() => {
        let isMounted = true;

        async function refreshBalance() {
            try {
                const data = await getBalance(false);
                if (!isMounted || !data || typeof data !== 'object') return;
                const row = data[coin];
                if (row) {
                    setLiveBalance({
                        available: parseBalanceQty(row.available ?? row.availableBalance),
                        onOrder: parseBalanceQty(row.onOrder)
                    });
                }
            } catch (err) {
                console.error('Erro ao atualizar saldo:', err);
            }
        }

        refreshBalance();
        return () => { isMounted = false; };
    }, [coin]);

    useEffect(() => {
        let isMounted = true;
        async function loadSymbolInfo() {
            try {
                const data = await getSymbol(symbol);
                if (!isMounted || !data) return;
                setStepSize(parseFloat(data.stepSize || 0) || 0);
                setMinNotional(parseFloat(data.minNotional || 0) || 0);
            } catch (err) {
                console.error('Erro ao carregar filtros do par:', err);
            }
        }
        loadSymbolInfo();
        return () => { isMounted = false; };
    }, [symbol]);

    useEffect(() => {
        let isMounted = true;

        const fetchPrice = async () => {
            try {
                if (coin === 'USDT' || coin === 'USDC' || coin === 'BUSD') {
                    if (isMounted) setPrice(1);
                    return;
                }
                const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`);
                const data = await response.json();
                const nextPrice = parseFloat(data.price || 0);
                if (isMounted && nextPrice > 0) {
                    setPrice(nextPrice);
                    return;
                }
                if (coin === 'HYPE') {
                    const responseCg = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=hyperliquid&vs_currencies=usd');
                    const dataCg = await responseCg.json();
                    const cgPrice = parseFloat(dataCg?.hyperliquid?.usd || 0);
                    if (isMounted && cgPrice > 0) setPrice(cgPrice);
                }
            } catch (err) {
                console.error('Erro ao buscar preço:', err);
            }
        };

        fetchPrice();
        const interval = setInterval(fetchPrice, 5000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [symbol, coin]);

    useEffect(() => {
        const computedPrice = parseFloat(price || 0);
        const brlValue = parseFloat(String(brlAmount || '').replace(',', '.') || 0);
        const rate = parseFloat(usdtBrlRate || 0);

        if (brlValue > 0 && rate > 0 && computedPrice > 0) {
            const usdtValue = brlValue / rate;
            let qtyValue = usdtValue / computedPrice;
            if (maxQuantity > 0 && qtyValue > maxQuantity)
                qtyValue = maxQuantity;
            qtyValue = roundToStep(qtyValue, stepSize);
            setTotalUSDT((qtyValue * computedPrice).toFixed(2));
            setQuantity(qtyValue.toFixed(8));
        } else {
            setTotalUSDT('0');
            setQuantity('0');
        }
    }, [brlAmount, price, limitPrice, orderType, usdtBrlRate, maxQuantity, stepSize]);

    async function loadUsdtBrlRate() {
        try {
            const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=USDTBRL');
            const data = await response.json();
            const rate = parseFloat(data.price || 0);
            if (rate > 0) setUsdtBrlRate(rate);
        } catch (err) {
            console.error('Erro ao carregar taxa USDT/BRL:', err);
        }
    }

    useEffect(() => {
        loadUsdtBrlRate();
        const interval = setInterval(loadUsdtBrlRate, 15000);
        return () => clearInterval(interval);
    }, []);

    function applyMaxSell() {
        const computedPrice = parseFloat(price || 0);
        const rate = parseFloat(usdtBrlRate || 0);
        if (!(maxQuantity > 0) || !(computedPrice > 0) || !(rate > 0)) return;
        const brl = maxQuantity * computedPrice * rate;
        setBrlAmount(brl.toFixed(2));
        setError('');
    }

    function applyPercentage(percent) {
        const computedPrice = parseFloat(price || 0);
        const rate = parseFloat(usdtBrlRate || 0);
        if (!(maxQuantity > 0) || !(computedPrice > 0) || !(rate > 0)) return;
        const qty = roundToStep(maxQuantity * (percent / 100), stepSize);
        if (!(qty > 0)) return;
        setBrlAmount((qty * computedPrice * rate).toFixed(2));
        setError('');
    }

    function handleBrlChange(e) {
        const value = e.target.value;
        if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
            setBrlAmount(value);
            setError('');
        }
    }

    function handleNext() {
        if (!brlAmount || parseFloat(String(brlAmount).replace(',', '.')) <= 0) {
            setError('Por favor, informe o valor em reais que deseja vender.');
            return;
        }
        if (!usdtBrlRate || usdtBrlRate <= 0) {
            setError('Não foi possível obter a cotação USDT/BRL no momento.');
            return;
        }
        const qty = parseFloat(quantity || 0);
        if (!(qty > 0) || qty > availableBalance + 1e-12) {
            setError(`Saldo insuficiente. Disponível: ${availableBalance.toFixed(8)} ${coin}`);
            return;
        }
        const enteredBrl = parseFloat(String(brlAmount).replace(',', '.'));
        const maxBrl = maxQuantity * parseFloat(price || 0) * parseFloat(usdtBrlRate || 0);
        if (maxBrl > 0 && enteredBrl > maxBrl + 0.05) {
            setBrlAmount(maxBrl.toFixed(2));
        }
        const notional = qty * parseFloat(price || 0);
        if (minNotional > 0 && notional < minNotional) {
            setError(`Valor abaixo do mínimo da Binance (≈ US$ ${minNotional.toFixed(2)}). Use "Vender tudo" ou aumente o valor.`);
            return;
        }
        setError('');
        setStep(2);
    }

    function handleBack() {
        if (step > 1) {
            setStep(step - 1);
            setError('');
        }
    }

    async function handleSell() {
        try {
            setLoading(true);
            setError('');

            const qty = roundToStep(Math.min(parseFloat(quantity || 0), maxQuantity), stepSize);
            if (!(qty > 0)) {
                setError(`Quantidade inválida para venda de ${coin}.`);
                return;
            }

            const order = {
                symbol: symbol,
                side: 'SELL',
                quantity: qty,
                limitPrice: null,
                options: {
                    type: 'MARKET'
                }
            };

            await placeOrder(order, false);
            setNotification({ type: 'success', text: 'Ordem de venda criada com sucesso!' });
            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            const errorMsg = err.response?.data?.message || 
                           err.response?.data?.msg ||
                           (typeof err.response?.data === 'string' ? err.response.data : null) ||
                           err.message || 
                           'Erro ao criar ordem de venda';
            setError(errorMsg);
            setNotification({ type: 'error', text: errorMsg });
        } finally {
            setLoading(false);
        }
    }

    const estimatedUsdt = availableBalance * parseFloat(price || 0);
    const estimatedBrl = estimatedUsdt * parseFloat(usdtBrlRate || 0);

    return (
        <main className="content">
            <div className="sell-screen-container">
                <div className="sell-screen-header">
                    <h1>Vender {coin}</h1>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="sell-content">
                    {!hasBalance && (
                        <div className="no-balance-message">
                            <div className="no-balance-icon">⚠️</div>
                            <h2>
                                {lockedBalance > 0
                                    ? `Seu ${coin} está bloqueado em ordens`
                                    : `Você não possui ${coin} disponível`}
                            </h2>
                            {lockedBalance > 0 ? (
                                <p>Há {lockedBalance.toFixed(8)} {coin} em ordens abertas. Cancele essas ordens para liberar o saldo e vender.</p>
                            ) : (
                                <p>Você precisa ter {coin} livre na carteira Spot para realizar uma venda.</p>
                            )}
                            <p className="balance-info">
                                Livre: {availableBalance.toFixed(8)} {coin}
                                {lockedBalance > 0 ? ` · Bloqueado: ${lockedBalance.toFixed(8)} ${coin}` : ''}
                            </p>
                            <button className="btn-primary" onClick={onClose}>
                                Voltar
                            </button>
                        </div>
                    )}

                    {hasBalance && (
                    <>
                    <div className="balance-info-box">
                        <div className="balance-label">Saldo Disponível</div>
                        <div className="balance-value">{availableBalance.toFixed(8)} {coin}</div>
                        <div className="balance-usdt">
                            ≈ ${estimatedUsdt.toFixed(2)} {quoteAsset}
                            {estimatedBrl > 0 && (
                                <span> · R$ {estimatedBrl.toFixed(2)}</span>
                            )}
                        </div>
                        {coin === 'BNB' && (
                            <p className="balance-info">A taxa da Binance sai do BNB. O app reserva uma pequena parte para a taxa ao vender tudo.</p>
                        )}
                    </div>

                    {step === 1 && (
                        <div className="form-section">
                            <div className="form-group">
                                <label>Tipo de Ordem</label>
                                <div className="order-type-buttons">
                                    <button
                                        className="order-type-btn active"
                                        type="button"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        <strong>Market</strong>
                                        <span>Venda imediata ao preço de mercado</span>
                                    </button>
                                </div>
                            </div>

                            <div className="price-display">
                                <div className="price-label">Preço Atual ({coin}/{quoteAsset})</div>
                                <div className="price-value">${parseFloat(price || 0).toLocaleString('pt-BR', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 8 
                                })}</div>
                            </div>

                            <div className="form-group">
                                <label>Valor em reais que deseja vender</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={brlAmount}
                                    onChange={handleBrlChange}
                                    placeholder="0,00"
                                />
                                <div className="percentage-buttons">
                                    <button type="button" onClick={() => applyPercentage(25)}>25%</button>
                                    <button type="button" onClick={() => applyPercentage(50)}>50%</button>
                                    <button type="button" onClick={() => applyPercentage(75)}>75%</button>
                                    <button type="button" onClick={applyMaxSell}>Vender tudo</button>
                                </div>
                            </div>

                            <div className="summary-box">
                                <div className="summary-row">
                                    <span>Total em {quoteAsset}:</span>
                                    <strong>${parseFloat(totalUSDT || 0).toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</strong>
                                </div>
                                <div className="summary-row">
                                    <span>Quantidade estimada ({coin}):</span>
                                    <span>{parseFloat(quantity || 0).toFixed(8)} {coin}</span>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <div className="sell-actions">
                                <button className="btn-secondary" onClick={onClose}>Cancelar</button>
                                <button 
                                    className="btn-primary btn-sell" 
                                    onClick={handleNext}
                                >
                                    Próximo
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="form-section">
                            <div className="confirmation-box">
                                <div className="confirmation-row">
                                    <span>Moeda:</span>
                                    <strong>{coin}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Tipo de Ordem:</span>
                                    <strong>Market</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Valor em reais:</span>
                                    <strong>R$ {parseFloat(String(brlAmount).replace(',', '.') || 0).toLocaleString('pt-BR', { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                    })}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Total em {quoteAsset}:</span>
                                    <strong>${parseFloat(totalUSDT || 0).toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })} {quoteAsset}</strong>
                                </div>
                                <div className="confirmation-row">
                                    <span>Quantidade estimada:</span>
                                    <strong>{parseFloat(quantity || 0).toFixed(8)} {coin}</strong>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <div className="sell-actions">
                                <button className="btn-secondary" onClick={handleBack}>Voltar</button>
                                <button 
                                    className="btn-primary btn-sell" 
                                    onClick={handleSell}
                                    disabled={loading}
                                >
                                    {loading ? 'Processando...' : 'Confirmar Venda'}
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </main>
    );
}

export default SellScreen;
